import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import { CURRENT_SECTION, CURRENT_STEP } from "../constants/storage";
import { NavigationContext } from "../contexts/Navigation";
import { scrollToTop } from "../helpers/common";
import { isSegmentInPath } from "../helpers/stringHelpers";
import usePathChunk from "../hooks/usePathChunk";
import useStorage from "../hooks/useStorage";
import {
  ADD_DRIVER,
  CURRENT_DRIVER_INDEX,
  getDriverSliceData,
} from "../redux/slices/DriverSlice";
import {
  ADD_VEHICLE,
  CURRENT_VEHICLE_INDEX,
  getVehicleSliceData,
} from "../redux/slices/VehicleSlice";

const NavigationProviders = ({ children }) => {
  const location = useLocation();
  const { t } = useTranslation();

  const { setLocalStorage } = useStorage();

  // State variable to enable/disable the "Continue" button
  const [continueDisabled, setContinueDisabled] = useState(false);

  // State variable to handle the "Continue" button loading
  const [isContinueBtnLoading, setIsContinueBtnLoading] = useState(false);

  // State variable for a callback function to execute before proceeding to the next step
  const [nextCallback, setNextCallback] = useState(null);

  // State variables for the current section and step
  const [currentSection, setCurrentSection] = useState("");
  const [currentStep, setCurrentStep] = useState(1);

  const [disabledTooltip, setDisabledTooltip] = useState("");

  // set footer next button text
  const [footerNextButtonText, setFooterNextButtonText] = useState(
    t("continue")
  );

  // Update local storage whenever currentStep or currentSection changes
  useEffect(() => {
    setLocalStorage(CURRENT_STEP, currentStep);
    setLocalStorage(CURRENT_SECTION, currentSection);
  }, [currentStep, currentSection]);

  // Function to enable/disable the "Continue" button
  const handleContinueDisabled = (isDisable) => {
    setContinueDisabled(isDisable);
  };

  // Function to handle, the "Continue" button loading
  const handleContinueButtonLoading = (isLoading) => {
    setIsContinueBtnLoading(isLoading);
  };

  // Function to register a callback function to be executed before proceeding to the next step
  const registerCallbackFunction = (callback) => {
    setNextCallback(callback);
  };

  // Save and exit callback function
  const handleSaveAndExitCallBack = async () => {
    if (!nextCallback) {
      return true;
    }

    if (currentSection === 2 && currentStep === 2) {
      return true;
    }

    if (await nextCallback()) {
      return true;
    }

    return false;
  };

  const handleNext = async () => {
    scrollToTop();
    if (nextCallback) nextCallback();
  };


  const handleFooterNextButtonText = (text) => {
    setFooterNextButtonText(text);
  };

  return (
    <NavigationContext.Provider
      value={{
        // Current section and step
        currentSection,
        currentStep,

        // State variable to enable/disable the "Continue" button
        continueDisabled,
        handleNext,
        // Setters for current section and step
        setCurrentSection,
        setCurrentStep,

        // Function to enable/disable the "Continue" button
        handleContinueDisabled,

        // Function to register a callback function to be executed before proceeding to the next step
        registerCallbackFunction,

        // section route
        currentSectionRoute: location.pathname,

        // button loader
        handleContinueButtonLoading,
        isContinueBtnLoading,

        // footer "Next" button text and handler
        footerNextButtonText,
        handleFooterNextButtonText,

        // function when user clicks on "Save and Exit" button
        handleSaveAndExitCallBack,

        disabledTooltip,
        setDisabledTooltip,
      }}
    >
      {children}
    </NavigationContext.Provider>
  );
};

export default NavigationProviders;
